if (!customElements.get('spotlight-search')) {
  customElements.define('spotlight-search', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-search-open]');
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('[data-search-results]');
      this.message = this.querySelector('[data-search-message]');
      this.status = this.querySelector('[data-search-status]');
      this.footer = this.querySelector('[data-search-footer]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      this.requestVersion = 0;
      this.activeIndex = -1;
      this.resultOptions = [];
      if (typeof this.dialog.showModal !== 'function') return;

      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', this.dialog.id);
      this.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('site-header')?.closeMenus();
        this.updateViewport();
        this.dialog.showModal();
        document.documentElement.classList.add('spotlight-search-open');
        this.input.focus({ preventScroll: true });
        this.input.select();
        this.onInput();
      }, options);
      this.querySelector('[data-search-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dialog.close();
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !event.isComposing) {
          event.preventDefault();
          event.stopPropagation();
          this.dialog.close();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('input:not([type="hidden"]), button:not(:disabled), a[href]:not([tabindex="-1"])')].filter((element) => element.getClientRects().length);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.cancelRequest();
        this.results.removeAttribute('aria-busy');
        this.input.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('spotlight-search-open');
        this.trigger.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('pointerdown', (event) => {
        this.backdropPointer = this.isBackdrop(event);
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropPointer && this.isBackdrop(event)) this.dialog.close();
      }, options);
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      }, options);
      this.input.addEventListener('input', (event) => {
        if (!event.isComposing) this.onInput();
      }, options);
      this.input.addEventListener('compositionstart', () => this.cancelRequest(), options);
      this.input.addEventListener('compositionend', () => this.onInput(), options);
      this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.results.addEventListener('pointermove', (event) => {
        const option = event.target.closest('[data-search-option]');
        if (option && event.pointerType === 'mouse') this.selectOption(this.resultOptions.indexOf(option));
      }, options);
      this.results.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.dialog.close();
      }, options);
      this.form.addEventListener('submit', (event) => {
        this.input.value = this.input.value.trim();
        if (!this.input.value) {
          event.preventDefault();
          this.onInput();
          this.input.focus();
        }
      }, options);
      window.visualViewport?.addEventListener('resize', () => this.updateViewport(), options);
      window.visualViewport?.addEventListener('scroll', () => this.updateViewport(), options);
    }

    updateViewport() {
      this.dialog.style.setProperty('--spotlight-viewport-height', `${window.visualViewport?.height || window.innerHeight}px`);
      this.dialog.style.setProperty('--spotlight-viewport-top', `${window.visualViewport?.offsetTop || 0}px`);
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    onInput() {
      this.cancelRequest();
      const term = this.input.value.trim();
      this.activeIndex = -1;
      this.resultOptions = [];
      this.results.replaceChildren();
      this.results.hidden = true;
      this.results.removeAttribute('aria-busy');
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      this.clearButton.hidden = !this.input.value.length;
      this.footer.hidden = !term;
      if (!term) {
        this.showMessage('Search by product name, part or SKU.');
        this.status.textContent = '';
        return;
      }
      const key = term.toLocaleLowerCase();
      if (this.cache.has(key)) {
        this.renderResults(this.cache.get(key));
        return;
      }
      this.showMessage('Searching…');
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Searching for products.';
      this.debounceTimer = setTimeout(() => this.fetchResults(term, key), 200);
    }

    async fetchResults(term, key) {
      const version = this.requestVersion;
      const request = new AbortController();
      this.pendingRequest = request;
      const timeout = setTimeout(() => request.abort(), 8000);
      try {
        const url = new URL(this.dataset.suggestUrl, window.location.origin);
        url.search = new URLSearchParams({
          q: term,
          'resources[type]': 'product',
          'resources[limit]': '6',
          'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor',
          'resources[options][unavailable_products]': 'last',
          section_id: 'spotlight-search-results',
        }).toString();
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (version !== this.requestVersion || !this.dialog.open) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-spotlight-results]');
        if (!content) throw new Error('Missing search results');
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(key, content.innerHTML);
        this.renderResults(content.innerHTML);
      } catch (error) {
        if (version !== this.requestVersion || !this.dialog.open) return;
        this.results.removeAttribute('aria-busy');
        this.showMessage('Suggestions are unavailable. Press Enter to search.');
        this.status.textContent = this.message.textContent;
      } finally {
        clearTimeout(timeout);
        if (this.pendingRequest === request) this.pendingRequest = null;
      }
    }

    renderResults(html) {
      this.results.innerHTML = html;
      this.resultOptions = [...this.results.querySelectorAll('[data-search-option]')];
      const count = this.resultOptions.length;
      this.results.removeAttribute('aria-busy');
      this.results.hidden = count === 0;
      this.input.setAttribute('aria-expanded', String(count > 0));
      this.message.hidden = count > 0;
      if (!count) this.showMessage('No matching products. Try another name, part or SKU.');
      this.status.textContent = count ? `${count} product suggestion${count === 1 ? '' : 's'} available. Use the up and down arrow keys to browse.` : this.message.textContent;
    }

    showMessage(text) {
      this.message.textContent = text;
      this.message.hidden = false;
    }

    onKeydown(event) {
      if (event.isComposing || !this.resultOptions.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const count = this.resultOptions.length;
        const next = this.activeIndex < 0 ? (event.key === 'ArrowDown' ? 0 : count - 1) : (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + count) % count;
        this.selectOption(next);
        this.resultOptions[next].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        this.resultOptions[this.activeIndex].querySelector('a').click();
      }
    }

    selectOption(index) {
      if (index < 0 || index === this.activeIndex) return;
      this.resultOptions[this.activeIndex]?.setAttribute('aria-selected', 'false');
      this.activeIndex = index;
      const selected = this.resultOptions[index];
      selected.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', selected.id);
    }

    cancelRequest() {
      this.requestVersion++;
      clearTimeout(this.debounceTimer);
      this.pendingRequest?.abort();
      this.pendingRequest = null;
    }

    disconnectedCallback() {
      this.cancelRequest();
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('spotlight-search-open');
    }
  });
}
